import React, { useState } from 'react';
import { Mail, ArrowRight, CheckCircle, AlertCircle } from 'lucide-react';
import { FiXCircle } from "react-icons/fi";


const WorkEmailCapture = ({
  setShowPremium
}) => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle, loading, success, error
  const [errorMessage, setErrorMessage] = useState('');

  const validateWorkEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return 'Please enter a valid email address';
    }
    
    // Common personal email domains to exclude
    const personalDomains = ['gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'icloud.com', 'aol.com'];
    const domain = email.split('@')[1]?.toLowerCase();
    
    if (personalDomains.includes(domain)) {
      return 'Please use your work email address';
    }
    
    return null;
  };

  const handleSubmit = async () => {
    
    const validationError = validateWorkEmail(email);
    if (validationError) {
      setErrorMessage(validationError);
      setStatus('error');
      return;
    }

    setStatus('loading');
    setErrorMessage('');

    // Simulate API call
    try {
      await new Promise(resolve => setTimeout(resolve, 1500));
      setStatus('success');
    } catch (error) {
      setStatus('error');
      setErrorMessage('Something went wrong. Please try again.');
    }
  };

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    if (status === 'error') {
      setStatus('idle');
      setErrorMessage('');
    }
  };

  if (status === 'success') {
    return (
      <div className="relative max-w-md mx-auto bg-zinc-900 rounded-2xl shadow-xl border border-zinc-800 p-8 text-center">
        <div className="absolute top-4 right-4">
        <button
          onClick={() => setShowPremium(false)}
          className="text-zinc-400 hover:text-white transition-colors duration-200 p-1 rounded-full"
        >
          <FiXCircle size={18} />
        </button>
      </div>
        <div className="w-16 h-16 bg-black rounded-full flex items-center justify-center mx-auto mb-6">
          <CheckCircle className="w-8 h-8 text-white" />
        </div>
        <h3 className="text-xl font-semibold text-white mb-2">
          Access Granted!
        </h3>
        <p className="text-zinc-400 mb-6">
          We've sent you access instructions at <span className="font-medium text-white">{email}</span>
        </p>
        
          <button 
            onClick={() => {
              setStatus('idle');
              setEmail('');
            }}
            className="text-zinc-400 hover:text-zinc-200 text-sm font-medium transition-colors mb-4"
          >
            Submit another email
          </button>
          
        
      </div>

    );
  }

  return (
    <div className="relative max-w-md mx-auto bg-zinc-900 rounded-2xl shadow-xl border border-zinc-800 p-8">
      <div className="absolute top-4 right-4">
        <button
          onClick={() => setShowPremium(false)}
          className="text-zinc-400 hover:text-white transition-colors duration-200 p-1 rounded-full"
        >
          <FiXCircle size={18} />
        </button>
      </div>
  <div className="text-center mb-8">
    <div className="w-16 h-16 bg-black rounded-full flex items-center justify-center mx-auto mb-4">
      <Mail className="w-8 h-8 text-white" />
    </div>
    <h2 className="text-2xl font-bold text-white mb-2">
      Get Early Access
    </h2>
    <p className="text-zinc-400">
      Enter your work email to unlock premium features
    </p>
  </div>

  <div className="space-y-4">
    <div className="space-y-2">
      <label htmlFor="work-email" className="block text-sm font-medium text-zinc-300">
        Work Email Address
      </label>
      <div className="relative">
        <input
          id="work-email"
          type="email"
          value={email}
          onChange={handleEmailChange}
          onInput={handleEmailChange}
          placeholder="you@company.com"
          className={`w-full px-4 py-3 border rounded-xl bg-zinc-800 text-white placeholder-zinc-500 focus:ring-2 focus:ring-white focus:border-transparent transition-all duration-200 ${
            status === 'error' 
              ? 'border-red-500 bg-red-950' 
              : 'border-zinc-700 hover:border-zinc-600 focus:border-white'
          }`}
          disabled={status === 'loading'}
        />
        {status === 'error' && (
          <AlertCircle className="absolute right-3 top-3.5 w-5 h-5 text-red-400" />
        )}
      </div>
      {status === 'error' && (
        <p className="text-sm text-red-500 flex items-center gap-1">
          {errorMessage}
        </p>
      )}
    </div>

    <button
      onClick={handleSubmit}
      disabled={!email.trim() || status === 'loading'}
      className="w-full bg-zinc-700 hover:bg-zinc-600 text-white font-medium py-3 px-4 rounded-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 group"
    >
      {status === 'loading' ? (
        <>
          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
          Processing...
        </>
      ) : (
        <>
          Get Access
          <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
        </>
      )}
    </button>
  </div>

  <div className="mt-6 text-center">
    <p className="text-xs text-zinc-500">
      We'll only use your email to grant feature access.{' '}
      <span className="text-zinc-300 font-medium">No spam, ever.</span>
    </p>
  </div>
</div>

  );
};

export default WorkEmailCapture;